import React, { useState, type FC, useEffect, useRef } from 'react';
import { LeftSquareFilled, RightSquareFilled } from '@ant-design/icons';
import cx from 'classnames';
import { Input } from 'antd';
import { Button } from '@g2u/ui-kit';
import { observer } from 'mobx-react-lite';

import { BookingStore } from 'store';
import { type WithOptionalDataTest } from 'types';
import { type TextAreaRef } from 'antd/es/input/TextArea';
import styles from './notepad.module.scss';

export namespace Notepad {
  export type Props = {
    isOpen?: boolean;
  } & WithOptionalDataTest;

  const InputSection: FC<{ idx: number; title?: string }> = observer((props) => {
    const { idx, title } = props;
    const { notes, setNotes } = BookingStore;
    const titleRef = useRef<TextAreaRef>(null);
    const descriptionRef = useRef<TextAreaRef>(null);
    const [titleValue, setTitleValue] = useState<string | undefined>(title);
    const [descriptionValue, setDescriptionValue] = useState<string>('');

    useEffect(() => {
      titleRef.current?.focus();
    }, []);

    return (
      <div className={styles.notepad__textareaSection}>
        <Input.TextArea
          rows={1}
          autoSize
          ref={titleRef}
          onPressEnter={(e) => {
            descriptionRef.current?.focus();
            e.preventDefault();
          }}
          onKeyDown={(e) => {
            if (e.keyCode === 40) {
              if (e.currentTarget.selectionStart === e.currentTarget.value.length) {
                descriptionRef.current?.focus();
                e.preventDefault();
              }
            }
          }}
          value={titleValue}
          onChange={(e) => {
            setTitleValue(e.target.value);
            const newNotes = [...notes];
            newNotes[idx] = e.target.value;
          }}
          className={cx(styles.notepad__textarea_title, styles.notepad__textareaControl)}
          placeholder="Customer Name"
        />
        <Input.TextArea
          autoSize
          ref={descriptionRef}
          onKeyDown={(e) => {
            if (e.keyCode === 38 && e.currentTarget?.selectionStart === 0) {
              titleRef.current?.focus();
              e.preventDefault();
            }
          }}
          className={cx(styles.notepad__textarea_description, styles.notepad__textareaControl)}
          placeholder="Problems and other quick notes."
          onChange={(e) => {
            setDescriptionValue(e.target.value);
          }}
        />
        <Button.$
          dataTestId="copy-notes"
          disabled={!descriptionValue}
          onClick={() => {
            setNotes(descriptionValue);
          }}
          type={Button.Type.Link}
        >
          Add text to the Job Description
        </Button.$>
      </div>
    );
  });

  export const $: FC<Props> = (props) => {
    const { isOpen = false, dataTestId } = props;

    const [titleValue, setTitleValue] = useState<string>();
    const [opened, setOpened] = useState<boolean>(isOpen);
    const [fieldsAmount, setFieldsAmount] = useState<number>(1);

    useEffect(() => {
      setOpened(isOpen);
    }, [isOpen]);

    useEffect(() => {
      fieldsAmount === 0 && setFieldsAmount(1);
    }, [fieldsAmount]);

    return (
      <div className={cx(styles.notepad, opened && styles.notepad_opened)} data-testid={dataTestId}>
        <div
          className={cx(styles.notepad__placeholder, opened && styles.notepad__placeholder_hidden)}
          data-testid="toggle-notepad-placeholder"
          onClick={() => {
            !opened && setOpened(!opened);
          }}
        >
          notepad
        </div>
        <div
          className={styles.notepad__tag}
          data-testid="toggle-notepad-tag"
          onClick={() => {
            setOpened(!opened);
          }}
        >
          {opened ? <RightSquareFilled /> : <LeftSquareFilled />}
        </div>
        <div className={styles.notepad__title}>
          <div className={styles.notepad__titleText}>notepad</div>
          <Button.$
            type={Button.Type.Link}
            size={Button.Size.Small}
            className={styles.notepad__titleLink}
            dataTestId="clear-all-notepads"
            onClick={() => {
              setFieldsAmount(0);
              setTitleValue(''); // set to ignore 'name' from the store if cleared manually
            }}
          >
            Clear All
          </Button.$>
        </div>
        <div className={styles.notepad__textarea}>
          {[...Array(fieldsAmount).keys()].map((i) => (
            <InputSection key={i} idx={i} title={titleValue} />
          ))}
        </div>
        <Button.$
          type={Button.Type.Dashed}
          dataTestId="clear-all-notepads"
          onClick={() => {
            setFieldsAmount(fieldsAmount + 1);
          }}
          className={styles.notepad__action}
        >
          + Add
        </Button.$>
      </div>
    );
  };
}
